import type {
  BreakpointColumnConfig,
  BreakpointRowConfig,
  BreakpointCellConfig,
  BreakpointWidthConfig,
} from "../types";
import {
  DEFAULT_BREAKPOINT_WIDTH,
  createDefaultColumnConfig,
  createDefaultRowConfig,
  createDefaultCellConfig,
} from "./tableConstants";
import { DEFAULT_BORDER } from "./styleUtils";

/**
 * Check if two values are equal (deep comparison for objects)
 */
const isEqual = (val1: any, val2: any): boolean => {
  if (val1 === val2) return true;
  if (typeof val1 !== typeof val2) return false;
  if (typeof val1 !== 'object' || val1 === null || val2 === null) return false;
  
  const keys1 = Object.keys(val1);
  const keys2 = Object.keys(val2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => isEqual(val1[key], val2[key]));
};

/**
 * Generic function to strip default values from config
 */
type ComparisonFn = (configValue: any, defaultValue: any, key: string) => boolean;

const stripDefaults = <T extends Record<string, any>>(
  config: T,
  defaults: T,
  customComparisons?: Record<string, ComparisonFn>
): Partial<T> | undefined => {
  const result: Partial<T> = {};
  let hasNonDefault = false;

  for (const [key, defaultValue] of Object.entries(defaults)) {
    const configValue = config[key as keyof T];
    
    // Use custom comparison if provided, otherwise use default comparison
    const customCompare = customComparisons?.[key];
    const isDifferent = customCompare
      ? customCompare(configValue, defaultValue, key)
      : typeof defaultValue === 'object' && defaultValue !== null
        ? !isEqual(configValue, defaultValue)
        : configValue !== defaultValue;

    if (isDifferent) {
      result[key as keyof T] = configValue;
      hasNonDefault = true;
    }
  }

  return hasNonDefault ? result : undefined;
};

/**
 * Strip default values from breakpoint width config
 */
export const stripDefaultBreakpointWidth = (config: BreakpointWidthConfig): Partial<BreakpointWidthConfig> | undefined => {
  return stripDefaults(config, DEFAULT_BREAKPOINT_WIDTH, {
    rowGap: (configValue, defaultValue) => configValue !== defaultValue && configValue !== undefined,
  });
};

/**
 * Strip values from breakpoint config that match the base (xs) config
 * This allows other breakpoints to override xs values, including setting back to "default"
 */
export const stripBreakpointWidthAgainstBase = (
  config: BreakpointWidthConfig, 
  baseConfig: BreakpointWidthConfig | undefined
): Partial<BreakpointWidthConfig> | undefined => {
  if (!baseConfig) {
    return stripDefaultBreakpointWidth(config);
  }
  
  return stripDefaults(config, baseConfig, {
    rowGap: (configValue, baseValue) => configValue !== undefined && configValue !== baseValue,
  });
};

/**
 * Strip default values from table settings
 * For xs: compare against global defaults
 * For other breakpoints: compare against xs values (to allow overriding xs)
 */
const DEFAULT_BORDER_COLORS = new Set(["#000000", "#000", "initial", "currentcolor", ""]);

const TABLE_SETTING_DEFAULTS: Record<string, any> = {
  tableBorderWidth: 1,
  tableBorderRadius: 0,
  tableRowGap: 0,
};

export const stripDefaultTableSettings = (settings: {
  tableWidths: Record<string, BreakpointWidthConfig>;
  tableBorderColor: string;
  tableBorderWidth: number;
  tableBorderRadius: number;
  tableRowGap?: number;
}): any => {
  const result: any = {};
  
  // Process table widths
  const xsConfig = settings.tableWidths?.xs;
  const widths: Record<string, Partial<BreakpointWidthConfig>> = {};
  
  const widthStrippers: Record<string, (config: BreakpointWidthConfig) => Partial<BreakpointWidthConfig> | undefined> = {
    xs: (config) => stripDefaultBreakpointWidth(config),
    default: (config) => stripBreakpointWidthAgainstBase(config, xsConfig),
  };

  for (const [bp, config] of Object.entries(settings.tableWidths)) {
    const stripper = widthStrippers[bp] || widthStrippers.default;
    const stripped = stripper(config);
    if (stripped && Object.keys(stripped).length > 0) {
      widths[bp] = stripped;
    }
  }
  
  if (Object.keys(widths).length > 0) {
    result.tableWidths = widths;
  }
  
  // Handle border color
  if (settings.tableBorderColor && !DEFAULT_BORDER_COLORS.has(settings.tableBorderColor.toLowerCase())) {
    result.tableBorderColor = settings.tableBorderColor;
  }
  
  // Handle other settings
  const settingsToCheck: Array<keyof typeof settings> = ['tableBorderWidth', 'tableBorderRadius', 'tableRowGap'];
  settingsToCheck.forEach(key => {
    const value = settings[key];
    if (value !== undefined && value !== TABLE_SETTING_DEFAULTS[key]) {
      result[key] = value;
    }
  });
  
  return result;
};

/**
 * Strip default values from column config
 */
export const stripDefaultColumnConfig = (config: BreakpointColumnConfig): Partial<BreakpointColumnConfig> | undefined => {
  return stripDefaults(config, createDefaultColumnConfig(), {
    border: (configValue, defaultValue) => configValue && !isEqual(configValue, defaultValue),
  });
};

/**
 * Strip default values from row config
 */
export const stripDefaultRowConfig = (config: BreakpointRowConfig): Partial<BreakpointRowConfig> | undefined => {
  return stripDefaults(config, createDefaultRowConfig(), {
    border: (configValue) => configValue && !isEqual(configValue, DEFAULT_BORDER),
    gapAfter: (configValue) => configValue !== undefined && configValue !== "",
  });
};

/**
 * Strip default values from cell config
 */
export const stripDefaultCellConfig = (config: BreakpointCellConfig): Partial<BreakpointCellConfig> | undefined => {
  return stripDefaults(config, createDefaultCellConfig(), {
    border: (configValue, defaultValue) => configValue && !isEqual(configValue, defaultValue),
  });
};

